// const API_BASE = `http://localhost:8000/chats`;

import { API } from "@/constants";
import { get, post, put } from "./api";

/**
 * Subscription reads are a background poll (mount + window-focus refetch), not a
 * user-initiated action. In environments where the subscription service is
 * absent (dev/local), the endpoints return 404. `skipGlobalErrorHandler` keeps
 * the axios response interceptor from firing a raw "Not Found" toast for these
 * calls — a failed poll must fail quietly, not stack noise at the user. Quiet
 * is not the same as permissive: the caller (useSubscription's addon check)
 * fails CLOSED on this error — a real fetch failure denies the entitlement,
 * the same as the add-on genuinely being absent. We still surface the failure
 * to the console for debugging and re-throw so TanStack Query keeps its own
 * error state (drives `subscriptionError`).
 */
export const getLatestSubscription = async (companyId: number) => {
  try {
    const subscription: any = await get(API.LATEST(companyId), {
      skipGlobalErrorHandler: true,
    });
    return subscription?.data || subscription;
  } catch (err) {
    console.warn("[subscription] latest fetch failed (toast suppressed):", err);
    throw err;
  }
};

/**
 * Levers (entitlement rules) for a subscription plan (`GET
 * /subscription-levers/subscription-plan/{planId}/levers`). The lever named
 * "USECASE" carries the use-case ids the plan is entitled to. Returns the raw
 * `data` payload (`{ leverDetails: [...] }`); the caller finds the USECASE lever
 * and maps its `masterData[].masterId`. Fails open quietly like the other
 * subscription polls — a missing service must not stack a toast.
 */
export interface SubscriptionLever {
  name?: string;
  masterData?: { masterId?: number; masterDataName?: string }[];
}
export interface SubscriptionLevers {
  leverDetails?: SubscriptionLever[];
}

export const getSubscriptionLevers = async (planId: number): Promise<SubscriptionLevers> => {
  try {
    const res = await get<{ data?: SubscriptionLevers } & SubscriptionLevers>(
      API.SUBSCRIPTION_LEVERS(planId),
      { skipGlobalErrorHandler: true },
    );
    return (res?.data ?? res) ?? {};
  } catch (err) {
    console.warn("[subscription] levers fetch failed (toast suppressed):", err);
    throw err;
  }
};

export const getUtilisationLevers = async (companySubscriptionId: number) => {
  try {
    const utilisationLevers: any = await get(API.UTILISATION_LEVERS(companySubscriptionId), {
      skipGlobalErrorHandler: true,
    });
    return utilisationLevers?.data || utilisationLevers;
  } catch (err) {
    console.warn("[subscription] utilisation fetch failed (toast suppressed):", err);
    throw err;
  }
};

/**
 * Active add-ons on a subscription (`GET
 * /company-subscription-addon/subscription/{subscriptionId}/active`). Each
 * entry's `addon.name` is a FEATURE entitlement id (e.g.
 * "AGENCY_USER_MANAGEMENT_DASHBOARD"). Fails quietly (no raw toast) like the
 * other subscription polls, but NOT permissively: the caller
 * (`useSubscription`'s `hasAddon`) treats a fetch failure the same as the
 * add-on genuinely being absent — see `getLatestSubscription`'s docstring
 * above for why quiet and permissive are different things.
 */
export interface SubscriptionAddon {
  id: number;
  companySubscriptionId: number;
  addon: {
    id: number;
    name: string;
    addonType: string;
    ruleValue?: number;
  };
  status: string;
}

export const getActiveAddons = async (subscriptionId: number): Promise<SubscriptionAddon[]> => {
  try {
    const res = await get<{ data?: SubscriptionAddon[] } & SubscriptionAddon[]>(
      API.SUBSCRIPTION_ADDONS_ACTIVE(subscriptionId),
      { skipGlobalErrorHandler: true },
    );
    return res?.data ?? [];
  } catch (err) {
    console.warn("[subscription] active addons fetch failed (toast suppressed):", err);
    throw err;
  }
};

/** One trial ending in the requested month (`GET /admin/company-subscription/trials-expiring`). */
export interface ExpiringTrial {
  companyId: number;
  /** The service does not always resolve the name; may be null. */
  companyName: string | null;
  subscriptionId: number;
  planName: string;
  /** Date-only, e.g. "2026-12-16". */
  trialEndDate: string;
  daysRemaining: number;
}

export interface TrialsExpiringResponse {
  trials: ExpiringTrial[];
}

/**
 * Admin list of companies whose trial ends in the month `monthOffset` months from
 * now (0 = this month). Loads with the page, which renders a failure inline, so
 * the global error toast is suppressed: one failure, one message.
 */
export const getTrialsExpiring = async (
  productName: string,
  monthOffset: number,
): Promise<TrialsExpiringResponse> => {
  // The service returns the list directly: `{ data: ExpiringTrial[], errors: [] }`.
  const res = await get<{ data?: ExpiringTrial[] }>(
    API.TRIALS_EXPIRING(productName, monthOffset),
    { skipGlobalErrorHandler: true },
  );
  // Fail loudly on an unexpected shape rather than show "no trials" for it.
  if (res?.data != null && !Array.isArray(res.data)) {
    throw new Error("Unexpected trials-expiring response shape");
  }
  return { trials: res?.data ?? [] };
};

/** One company's current subscription for a product (`GET /admin/company-subscription/status`). */
export interface CompanySubscriptionStatus {
  companyId: number;
  companyName: string | null;
  subscriptionId: number;
  productName: string;
  status: string;
  subscriptionPlanId: number;
  planName: string;
  managedBySarasOnly: boolean;
  nextBillingDate: number | null;
  /**
   * Date-only calendar day (a Java `LocalDate`), e.g. "2026-12-16": the service's
   * own day for the trial end, not an instant. Parse it with `toDate` from
   * `@/utils/date`, never `new Date()`, which reads it as UTC midnight.
   */
  trialEndDate: string | null;
  currentUsage: {
    ruleName: string;
    /** Numeric strings, e.g. "1700.0". */
    ruleValue: string;
    usageValue: string;
    percentageOfUsage: number;
  } | null;
  activeAddOns: { id: number; status: string; addon: { name: string; ruleValue: number } }[];
}

/**
 * Admin read of one company's subscription for `productName`. Resolves to `null`
 * when the company has none. The global error toast is suppressed because the
 * subscriptions table renders the failure inline, per row.
 */
export const getCompanySubscriptionStatus = async (
  productName: string,
  companyId: number,
): Promise<CompanySubscriptionStatus | null> => {
  const res = await get<{ data?: CompanySubscriptionStatus | null }>(
    API.ADMIN_SUBSCRIPTION_STATUS(productName, companyId),
    { skipGlobalErrorHandler: true },
  );
  return res?.data ?? null;
};

/*
 * Admin subscription actions. User-initiated, so a failure goes through the
 * global error toast — callers must not toast again.
 */

/** One add-on definition (`GET /addons`). For a credit pack, `ruleValue` is the credits it adds. */
export interface Addon {
  id: number;
  name: string;
  addonType: string;
  ruleValue: number | null;
  price: number | null;
  product: { id: number; productName: string } | null;
}

/**
 * Every add-on definition across products. The global error toast is suppressed
 * because the "Add credits" dialog renders the failure inline.
 */
export const getAddons = async (): Promise<Addon[]> => {
  const res = await get<{ data?: Addon[] }>(API.ADDONS, { skipGlobalErrorHandler: true });
  return res?.data ?? [];
};

/** Buys `quantity` of an add-on (e.g. a credit pack) for a company. */
export const purchaseAddon = (companyId: number, addonId: number, quantity = 1) =>
  post(API.SUBSCRIPTION_ADDON_PURCHASE(companyId, addonId, quantity));

/**
 * Moves a company's trial end date. `trialExtendedTo` is "dd/MM/yyyy", e.g.
 * "16/12/2026". Sent as query params: the endpoint has no request body.
 */
export const extendTrial = ({
  companyId,
  productName,
  trialExtendedTo,
}: {
  companyId: number;
  productName: string;
  trialExtendedTo: string;
}) => put(API.SUBSCRIPTION_EXTEND_TRIAL(companyId, productName, trialExtendedTo));

/** One plan from `GET /subscription-plan`. */
export interface SubscriptionPlanOption {
  id: number;
  name: string;
  description?: string | null;
}

type PlanPage = { content?: SubscriptionPlanOption[] };

/**
 * Saras-managed plans for a product, for the admin "Change subscription" dialog.
 * The endpoint returns a Spring page (`{ content: [...], totalElements, ... }`);
 * the `data` envelope the other subscription endpoints use is accepted too. The
 * global error toast is suppressed because the dialog renders the failure inline.
 */
export const getSubscriptionPlans = async (productId: number): Promise<SubscriptionPlanOption[]> => {
  const res = await get<PlanPage & { data?: PlanPage | SubscriptionPlanOption[] }>(
    API.SUBSCRIPTION_PLANS(productId),
    { skipGlobalErrorHandler: true },
  );
  const plans = res?.content ?? (Array.isArray(res?.data) ? res.data : res?.data?.content);
  return Array.isArray(plans) ? plans : [];
};

/**
 * Puts a company on a manually managed plan (e.g. Enterprise). Sent as query
 * params: the endpoint has no request body.
 */
export const associateManualPlan = ({ companyId, subscriptionPlanId }: { companyId: number; subscriptionPlanId: number }) =>
  post(API.SUBSCRIPTION_ASSOCIATE_MANUAL_PLAN(companyId, subscriptionPlanId));
